import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { primaryColor } from "../../../utils/comon";

const MenuHierarchySelect = ({
    label,
    options = [],
    value = [],
    onChange,
    placeholder = "Select Menus",
    error,
    errorMessage,
    required = false,
    isDisabled = false
}) => {
    const [open, setOpen] = useState(false);
    const [isAbove, setIsAbove] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, bottom: 0, maxHeight: 350 });
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);

    // Group the options based on parentId
    const groupedOptions = options.filter(opt => opt.parentId === 0 || !opt.parentId).map(parent => {
        return {
            header: parent.menuName,
            items: [
                { label: parent.menuName, value: parent.menuListId || parent.id, isChild: false },
                ...options.filter(child => child.parentId === (parent.menuListId || parent.id)).map(child => ({
                    label: child.menuName,
                    value: child.menuListId || child.id,
                    isChild: true
                }))
            ]
        };
    });

    const updateCoords = () => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            
            const spaceBelow = window.innerHeight - rect.bottom - 20; // 20px padding
            const spaceAbove = rect.top - 20;

            let renderAbove = false;
            let finalMaxHeight = 350;

            if (spaceBelow < 350 && spaceAbove > spaceBelow) {
                renderAbove = true;
                finalMaxHeight = Math.min(350, spaceAbove);
            } else {
                renderAbove = false;
                finalMaxHeight = Math.min(350, spaceBelow > 100 ? spaceBelow : 150);
            }

            setCoords({
                top: rect.top + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
                bottom: rect.bottom + window.scrollY,
                maxHeight: finalMaxHeight
            });
            setIsAbove(renderAbove);
        }
    };

    useEffect(() => {
        if (open) {
            updateCoords();
            window.addEventListener("scroll", updateCoords, true);
            window.addEventListener("resize", updateCoords);
        }
        return () => {
            window.removeEventListener("scroll", updateCoords, true);
            window.removeEventListener("resize", updateCoords);
        };
    }, [open]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target) &&
                dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (val) => {
        let currentValues = Array.isArray(value) ? [...value] : [];
        const isSelected = currentValues.includes(val);
        const clickedOpt = options.find(o => (o.menuListId || o.id) === val);
        const isParent = clickedOpt && (clickedOpt.parentId === 0 || !clickedOpt.parentId);

        if (isSelected) {
            currentValues = currentValues.filter(v => v !== val);
            if (isParent) {
                const childIds = options.filter(child => child.parentId === val).map(child => child.menuListId || child.id);
                currentValues = currentValues.filter(v => !childIds.includes(v));
            } else {
                const parentId = clickedOpt?.parentId;
                if (parentId) {
                    currentValues = currentValues.filter(v => v !== parentId);
                }
            }
        } else {
            currentValues.push(val);
            if (isParent) {
                const childIds = options.filter(child => child.parentId === val).map(child => child.menuListId || child.id);
                childIds.forEach(id => {
                    if (!currentValues.includes(id)) currentValues.push(id);
                });
            } else {
                const parentId = clickedOpt?.parentId;
                if (parentId) {
                    const siblingIds = options.filter(child => child.parentId === parentId).map(child => child.menuListId || child.id);
                    const allSiblingsSelected = siblingIds.every(id => currentValues.includes(id));
                    if (allSiblingsSelected && !currentValues.includes(parentId)) {
                        currentValues.push(parentId);
                    }
                }
            }
        }
        onChange(currentValues);
    };

    const getDisplayValue = () => {
        if (Array.isArray(value) && value.length > 0) {
            const selectedLabels = value.map(v => {
                const opt = options.find(o => (o.menuListId || o.id) === v);
                return opt ? opt.menuName : '';
            }).filter(Boolean);
            return selectedLabels.join(", ");
        }
        return placeholder;
    };

    const dropdownContent = open && (
        <div
            ref={dropdownRef}
            className={`fixed z-[99999] bg-white border border-[#E2E8F0] rounded-xl shadow-2xl overflow-y-auto custom-scrollbar`}
            style={{
                top: isAbove ? coords.top - 12 : coords.bottom + 12,
                left: coords.left,
                width: coords.width,
                maxHeight: coords.maxHeight,
                transform: isAbove ? "translateY(-100%)" : "none"
            }}
        >
            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 5px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
            `}</style>
            <div className="py-2">
                {groupedOptions.map((group, gIdx) => (
                    <div key={gIdx} className="mb-2 last:mb-0">
                        {/* Group Header */}
                        <div className="px-4 py-2 text-[14px] font-extrabold text-[#0F172A]">
                            {group.header}
                        </div>
                        {/* Group Items */}
                        {group.items.map(opt => (
                            <div
                                key={opt.value}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelect(opt.value);
                                }}
                                className={`px-4 py-2.5 flex items-center gap-3 cursor-pointer transition-colors duration-150 hover:bg-gray-50 ${opt.isChild ? 'pl-10' : ''}`}
                            >
                                <div 
                                    className={`w-4 h-4 rounded flex items-center justify-center transition-colors ${value.includes(opt.value) ? 'border-transparent text-white' : 'border-[#94A3B8] border bg-white'}`}
                                    style={{ backgroundColor: value.includes(opt.value) ? primaryColor() : '' }}
                                >
                                    {value.includes(opt.value) && (
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </div>
                                <span className={`text-[13px] ${value.includes(opt.value) ? 'font-medium' : 'text-[#475569]'}`} style={{ color: value.includes(opt.value) ? primaryColor() : '' }}>
                                    {opt.label}
                                </span>
                            </div>
                        ))}
                    </div>
                ))}
                {groupedOptions.length === 0 && (
                    <div className="px-4 py-3 text-xs text-gray-400 text-center italic">
                        No options available
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div
            className={`w-full relative outline-none ${isDisabled ? "opacity-60 pointer-events-none" : ""}`}
            ref={containerRef}
            tabIndex={0}
        >
             {label && (
                <label className="text-xs font-semibold text-[#0F172A] dark:text-textprimary mb-1 block uppercase tracking-tight">
                    {label} {required && <span className="text-red-700">*</span>}
                </label>
            )}

            <div
                onClick={() => !isDisabled && setOpen(!open)}
                className={`border border-slate-200 dark:border-bordercolor rounded-md px-3 py-2 w-full text-sm bg-[#F8FAFC] dark:bg-actionitembg cursor-pointer flex justify-between items-center transition-all duration-200 ${error ? "border-red-500" : "hover:border-gray-400"}`}
            >
                <div className="flex items-center gap-2 overflow-hidden mr-2">
                    <span className={`truncate ${(Array.isArray(value) && value.length > 0) ? "text-[#0F172A] dark:text-textprimary" : "text-gray-400"}`}>
                        {getDisplayValue()}
                    </span>
                </div>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`text-gray-400 text-xs transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                />
            </div>

            {error && (
                <p className="text-red-500 text-[10px] mt-1 font-medium">
                    {typeof error === 'string' ? error : errorMessage}
                </p>
            )}

            {open && createPortal(dropdownContent, document.body)}
        </div>
    );
};

export default MenuHierarchySelect;
